<template>
	<div>
		<div v-if="errorMessage" class="error-message width-100 display-flex flex-align-center pl-1">
			Error while getting data: <br />{{ errorMessage }}
		</div>
		<NoDataFoundMessage v-else-if="showNoDataFoundMessage" />
		<div v-else>
			<SelectAllMultiSelect v-if="allowMultipleSelection" :isAllSelected="isAllSelected" @select-all="onSelectAll" />
			<div v-for="(item, index) in filteredFieldParams" :key="item.value">
				<MaybeUpgradeRequiredIndicator v-if="item.required_entitlement" :billingFeatureId="item.required_entitlement">
					<template #default="{ indicator, isUpgradeRequired }">
						<ParamsListItem
							:indicator="indicator"
							:isUpgradeRequired="isUpgradeRequired"
							:item="item"
							v-on="$listeners"
							@select-item="onItemClick"
							@focus-on-item="onInputBlur"
							:isFocused="isFocused(item)"
							:isSelected="isItemSelected(item)"
							:key="item.option + '_' + item.value"
							:showItemButtonFunc="showItemButtonFunc"
							:additionalInfo="getAdditionalInfo(item)"
							:hideValueDescription="hideValueDescription"
							:allowMultipleSelection="allowMultipleSelection"
							:class="{ 'mb-2': filteredFieldParams.length - 1 !== index }"
						/>
					</template>
				</MaybeUpgradeRequiredIndicator>
				<ParamsListItem
					v-else
					:item="item"
					v-on="$listeners"
					@select-item="onItemClick"
					@focus-on-item="onInputBlur"
					:isFocused="isFocused(item)"
					:isSelected="isItemSelected(item)"
					:key="item.option + '_' + item.value"
					:showItemButtonFunc="showItemButtonFunc"
					:additionalInfo="getAdditionalInfo(item)"
					:hideValueDescription="hideValueDescription"
					:allowMultipleSelection="allowMultipleSelection"
					:class="{ 'mb-2': filteredFieldParams.length - 1 !== index }"
				/>
			</div>
			<InsertDataLoadMore
				v-on="$listeners"
				:isLoading="isLoading"
				:isFiltered="isFiltered"
				:hasMoreData="hasMoreData"
				:totalItemsLength="totalItemsLength"
				:totalItemsOnDisplay="totalItemsOnDisplay"
			/>
		</div>
		<MaybeUpgradeRequiredIndicator :billingFeatureId="listButtonBillingFeatureId" v-if="listButtonText && listButtonBillingFeatureId">
			<template #default="{ indicator, isUpgradeRequired }">
				<AnemicButton @click="(e) => bottomButtonClick(e, isUpgradeRequired, indicator)" class="action-btn">
					{{ listButtonText }}
					<component v-if="isUpgradeRequired" class="ml-2" :is="indicator" :billingFeatureId="listButtonBillingFeatureId" iconOnly />
				</AnemicButton>
			</template>
		</MaybeUpgradeRequiredIndicator>
		<AnemicButton v-if="listButtonText && !listButtonBillingFeatureId" @click="(e) => bottomButtonClick(e)" class="action-btn">
		</AnemicButton>
	</div>
</template>

<script>
import MaybeUpgradeRequiredIndicator from '@/components/Billing/MaybeUpgradeRequiredIndicator.vue';
import InsertDataLoadMore from '@/components/DataInput/DataInputMenu/MenuLists/InsertDataLoadMore';
import ParamsListItem from '@/components/DataInput/DataInputMenu/MenuLists/ParamsList/ParamsListItem';
import SelectAllMultiSelect from '@/components/DataInput/DataInputMenu/MenuLists/SelectAllMultiSelect';
import NoDataFoundMessage from '@/components/DataInput/DataInputMenu/NoDataFoundMessage';
import { SET_VALUE_FROM_EXTERNAL_SOURCE } from '@/components/DataInput/DataInputMenu/helpers/data-input-menu.const';
import UserMatchingService from '@/utils/UserMatchingService';

export default {
	name: 'FieldParamsList',
	components: {
		ParamsListItem,
		NoDataFoundMessage,
		InsertDataLoadMore,
		SelectAllMultiSelect,
		MaybeUpgradeRequiredIndicator,
	},
	props: {
		isLoading: { type: Boolean },
		fieldParams: { type: Array },
		searchString: { type: String },
		errorMessage: { type: String },
		hasMoreData: { type: Boolean },
		listButtonText: { type: String },
		showItemButtonFunc: { type: Function },
		hideValueDescription: { type: Boolean },
		allowMultipleSelection: { type: Boolean },
		listButtonBillingFeatureId: { type: String, default: null },
		selectedParams: { type: [Array, String, Number, Object, Boolean] },
	},
	computed: {
		USER_MATCHING_TYPE() {
			return UserMatchingService.USER_MATCHING_TYPE;
		},
		showNoDataFoundMessage() {
			return !(this.filteredFieldParams?.length > 0) && !this.hasMoreData;
		},
		filteredFieldParams() {
			if (!this.isFiltered) {
				return this.fieldParams;
			}
			return this.fieldParams.filter((param) => {
				if (typeof param === 'string') {
					return param.toLowerCase().includes(this.searchString.toLowerCase());
				}
				if (typeof param === 'object') {
					return Object.values(param).some(
						(objVal) => typeof objVal === 'string' && objVal.toLowerCase().includes(this.searchString.toLowerCase())
					);
				}
			});
		},
		totalItemsLength() {
			return this.fieldParams?.length || 0;
		},
		totalItemsOnDisplay() {
			return this.filteredFieldParams?.length || 0;
		},
		isFiltered() {
			return Boolean(this.searchString && this.searchString.length > 0);
		},
		isAllSelected() {
			return this.filteredFieldParams?.every((x) => this.isItemSelected(x));
		},
	},
	methods: {
		bottomButtonClick(event, isUpgradeRequired = null, indicator = null) {
			if (isUpgradeRequired && indicator?.setup) {
				indicator.setup().openUpgrade(event, this.billingFeatureId);
			} else {
				this.$emit('list-btn-clicked');
			}
		},
		getAdditionalInfo(item) {
			if (item.type == this.USER_MATCHING_TYPE) {
				return 'User matching';
			}
		},
		isFocused(item) {
			return item === this.focusedMenuItem;
		},
		isItemSelected(item) {
			if (Array.isArray(this.selectedParams)) {
				return this.selectedParams.some((selectedParam) => selectedParam.value === item.value);
			} else {
				return item.value === this.selectedParams;
			}
		},
		onInputBlur() {
			// Called from Parent
			this.focusedMenuItem = null;
		},
		selectFocusedItem() {
			// Called from Parent
			if (this.focusedMenuItem) {
				this.onItemClick(this.focusedMenuItem);
			}
		},
		onSelectAll() {
			if (this.allowMultipleSelection) {
				if (this.isAllSelected) {
					this.removeAllItemsFromMultiSelect();
				} else {
					this.addAllItemsToMultiSelect();
				}
			}
		},
		onItemClick(item) {
			// Called from Parent
			if (this.allowMultipleSelection) {
				this.isItemSelected(item) ? this.removeItemFromMultiSelect(item) : this.addItemToMultiSelect(item);
			} else {
				this.setExternalSourceValue(item);
			}
		},
		removeItemFromMultiSelect(item) {
			const allSelectedItems = this.selectedParams || [];
			const newSelectedItems = allSelectedItems.filter((field) => field.value !== item.value);
			this.setExternalSourceValue(newSelectedItems);
		},
		addItemToMultiSelect(item) {
			const allSelectedItems = Array.isArray(this.selectedParams) ? this.selectedParams : [];
			const newSelectedItems = [...allSelectedItems, item];
			this.setExternalSourceValue(newSelectedItems);
		},
		addAllItemsToMultiSelect() {
			this.setExternalSourceValue([...this.filteredFieldParams]);
		},
		removeAllItemsFromMultiSelect() {
			this.setExternalSourceValue([]);
		},
		setExternalSourceValue(val) {
			this.$emit(SET_VALUE_FROM_EXTERNAL_SOURCE, val);
		},
		focusedMenuItemIndex() {
			return this.filteredFieldParams.findIndex((param) => param === this.focusedMenuItem);
		},
		focusOnNextMenuItem() {
			// Called from Parent
			const focusedItemIndex = this.focusedMenuItemIndex();
			const upNextItemIndex = this.filteredFieldParams?.length > focusedItemIndex + 1 ? focusedItemIndex + 1 : 0;
			this.focusedMenuItem = this.filteredFieldParams[upNextItemIndex];
		},
		focusOnPreviousMenuItem() {
			// Called from Parent
			const focusedItemIndex = this.focusedMenuItemIndex();
			const upNextItemIndex = focusedItemIndex - 1 > -1 ? focusedItemIndex - 1 : this.filteredFieldParams?.length - 1;
			this.focusedMenuItem = this.filteredFieldParams[upNextItemIndex];
		},
	},
	data() {
		return {
			focusedMenuItem: null,
		};
	},
};
</script>

<style scoped lang="scss">
.error-message {
	font-size: 12px;
	color: red;
}

.action-btn {
	width: 100% !important;
	height: 44px !important;
	padding: 0px !important;
	background: var(--ask-for-ingreation-background) !important;
	font-size: 14px !important;
	text-transform: none !important;
	color: var(--publish-button-background) !important;
}
</style>
